import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import axios from 'axios';
import './DeveloperPortal.css';

interface RegisterOAuthAppProps {
  onBack: () => void;
  onSuccess: () => void;
  onLogout: () => void;
}

const RegisterOAuthApp: React.FC<RegisterOAuthAppProps> = ({ onBack, onSuccess, onLogout }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    description: '',
    redirect_uris: '',
    allowed_domains: '',
    scopes: ['general:access']  // Scopes are optional - all clients get general access
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Parse redirect URIs and allowed domains
      const redirectUrisList = newClient.redirect_uris
        .split(',')
        .map(uri => uri.trim())
        .filter(uri => uri.length > 0);
      
      const allowedDomainsList = newClient.allowed_domains
        .split('\n')
        .map(domain => domain.trim())
        .filter(domain => domain.length > 0);

      if (redirectUrisList.length === 0) {
        setError('At least one redirect URI is required');
        setLoading(false);
        return;
      }

      if (allowedDomainsList.length === 0) {
        setError('At least one allowed domain is required');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${config.API_BASE_URL}/oauth/portal/register-json`, {
        name: newClient.name,
        description: newClient.description,
        redirect_uris: redirectUrisList,
        allowed_domains: allowedDomainsList,
        scopes: newClient.scopes
      });

      if (response.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      let errorMessage = 'Failed to register OAuth application';
      
      if (err.response?.data?.detail) {
        // Handle both string and object error details
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors array
          errorMessage = err.response.data.detail.map((error: any) => 
            typeof error === 'string' ? error : error.msg || error.message || 'Validation error'
          ).join(', ');
        } else if (typeof err.response.data.detail === 'object') {
          errorMessage = err.response.data.detail.msg || err.response.data.detail.message || 'Validation error';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Scopes are no longer user-selectable - all clients get general access

  if (success) {
    return (
      <div className="developer-portal">
        <div className="portal-header">
          <div className="portal-header-content">
            <h1>Business Portal</h1>
            <div className="user-info">
              <span>Welcome, {user?.email}</span>
              <button onClick={onLogout} className="btn-secondary">Logout</button>
            </div>
          </div>
        </div>
        
        <div className="portal-content">
          <div className="success-message">
            <h2>✅ OAuth Application Registered Successfully!</h2>
            <p>Your application has been registered. Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="developer-portal">
      <div className="portal-header">
        <div className="portal-header-content">
          <h1>Business Portal</h1>
          <div className="user-info">
            <span>Welcome, {user?.email}</span>
            <button onClick={onLogout} className="btn-secondary">Logout</button>
          </div>
        </div>
      </div>
      
      <div className="portal-navigation">
        <button onClick={onBack} className="btn-secondary">← Back to Dashboard</button>
      </div>
      
      <div className="portal-content">
        <div className="register-form-container">
          <h2>Register New OAuth Application</h2>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="name">Application Name *</label>
              <input
                type="text"
                id="name"
                value={newClient.name}
                onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="My Restaurant App"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={newClient.description}
                onChange={(e) => setNewClient(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of your application"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="redirect_uris">Redirect URIs *</label>
              <input
                type="text"
                id="redirect_uris"
                value={newClient.redirect_uris}
                onChange={(e) => setNewClient(prev => ({ ...prev, redirect_uris: e.target.value }))}
                required
                placeholder="https://myapp.com/callback, https://myapp.com/auth/callback"
              />
              <small>Comma-separated list of allowed redirect URIs</small>
            </div>

            <div className="form-group">
              <label htmlFor="allowed_domains">Allowed Domains *</label>
              <textarea
                id="allowed_domains"
                value={newClient.allowed_domains}
                onChange={(e) => setNewClient(prev => ({ ...prev, allowed_domains: e.target.value }))}
                required
                placeholder="myapp.com&#10;www.myapp.com&#10;localhost:3000"
                rows={3}
              />
              <small>One domain per line</small>
            </div>

            {/* Scopes removed - API provides general access to all clients */}

            <div className="form-actions">
              <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Registering...' : 'Register Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterOAuthApp;
